// src/pages/MyBookings.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useUser } from "../context/UserContext";
import {
  getUserBookingActiveGroupsByUserId,
  getUserBookingHistoryGroupsByUserId,
  resolveUserIdForAuthenticatedUser,
  cancelBookingForUser,
  cancelRequestForUser,
  cancelScheduledPlanForUser,
} from "../api/bookingApi";
import { listActiveUserOpsTripRequests } from "../api/opsBookingApi";
import { getSearchResultsConfig } from "../api/vehicleApi";
import { getVehicleById } from "../api/vehiclesApi";

/* ───────── Formatting ───────── */
const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
    Number.isFinite(n) ? n : 0
  );

function formatDateWithSuffix(dateStr) {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "N/A";
  const day = d.getDate();
  const month = d.toLocaleString("en-GB", { month: "long" });
  const year = d.getFullYear();
  const suf =
    day >= 11 && day <= 13 ? "th" : ["th", "st", "nd", "rd"][Math.min(day % 10, 4)] || "th";
  return `${day}${suf} ${month} ${year}`;
}
function formatTime12(timeStr) {
  if (!timeStr) return "N/A";
  const [hh, mm = "0"] = String(timeStr).split(":");
  const h = parseInt(hh, 10);
  if (isNaN(h)) return "N/A";
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(parseInt(mm, 10)).padStart(2, "0")} ${ampm}`;
}

const n = (x) => (Number.isFinite(Number(x)) ? Number(x) : 0);

const StarOutlineIcon = ({ className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3.5l2.47 4.99 5.51.48-4.16 3.6 1.25 5.43L12 15.9l-4.07 2.1 1.25-5.43-4.16-3.6 5.51-.48L12 3.5z" />
  </svg>
);

/* ───────── Shared plan helpers ───────── */
function normStr(s) {
  return (s ?? "").toString().trim();
}

function makeTripKey(it) {
  const stops = Array.isArray(it?.destinations)
    ? it.destinations.map((s) => normStr(s)).filter(Boolean)
    : [];
  return JSON.stringify({
    pickupLocation: normStr(it?.pickupLocation),
    destinations: stops,
    destination: normStr(it?.destination || it?.finalDestination),
    pickupDate: normStr(it?.pickupDate),
    pickupTime: normStr(it?.pickupTime),
    dropDate: normStr(it?.dropDate),
    dropTime: normStr(it?.dropTime),
  });
}

function parsePlanDateTime(dateStr, timeStr) {
  const d = (dateStr || "").trim();
  if (!d) return null;
  const t = (timeStr || "").trim();
  try {
    if (t) {
      const iso = `${d}T${t.length === 5 ? `${t}:00` : t}`;
      const dt = new Date(iso);
      return isNaN(dt.getTime()) ? null : dt;
    }
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? null : dt;
  } catch {
    return null;
  }
}

function isPlanPast(g) {
  const end =
    parsePlanDateTime(g.dropDate, g.dropTime) || parsePlanDateTime(g.pickupDate, g.pickupTime);
  if (!end) return false;
  return end.getTime() < Date.now();
}

const MS_48H = 48 * 60 * 60 * 1000;

function isPlanHistoryDue(g) {
  const end =
    parsePlanDateTime(g.dropDate, g.dropTime) || parsePlanDateTime(g.pickupDate, g.pickupTime);
  if (!end) return false;
  return end.getTime() + MS_48H <= Date.now();
}

function isRequestItem(it) {
  return Boolean(it && (it.__kind === "request" || it?.requestId != null));
}
function asPositiveInt(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
}
function getCancelableRequestId(it) {
  if (!it) return null;
  const financeCase = getFinanceCaseForItem(it);
  return (
    asPositiveInt(it?.cancelRequestId) ??
    asPositiveInt(it?.requestId) ??
    asPositiveInt(it?.bookingMatchRequestId) ??
    asPositiveInt(financeCase?.requestId) ??
    null
  );
}
function pickScheduledRequestId(...candidates) {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      for (const value of candidate) {
        const id = asPositiveInt(value);
        if (id) return id;
      }
      continue;
    }
    const id = asPositiveInt(candidate);
    if (id) return id;
  }
  return null;
}
function isRideNowPlanKey(planKey) {
  return String(planKey || "").trim().toUpperCase().startsWith("RIDE_NOW_");
}
function isRideNowGroup(g) {
  return g.items.some((it) => isRideNowPlanKey(it?.planKey));
}
function isScheduledOpsGroup(g) {
  return g.items.some((it) => it?.__source === "scheduled_ops");
}
function isScheduledPlanGroup(g) {
  return g.items.some((it) => {
    if (it?.__source === "scheduled_ops") return true;
    const planKey = String(it?.planKey || "").trim();
    return !!planKey && !isRideNowPlanKey(planKey);
  });
}
function isScheduledBookingItem(it) {
  return !!it && !isRequestItem(it) && !isRideNowPlanKey(it?.planKey);
}
function getItemStatus(it) {
  const ride = String(it?.rideStatus || "").toUpperCase();
  const status = String(it?.status || "").toUpperCase();
  if (terminalLike(status)) return status;
  return ride || status;
}
function isCancelledItem(it) {
  const s = getItemStatus(it);
  return s === "CANCELLED" || s === "EXPIRED" || s === "UNFULFILLED";
}
function hasBookingStarted(bk) {
  if (!bk || isRequestItem(bk)) return false;
  const start = parsePlanDateTime(bk?.pickupDate || null, bk?.pickupTime || null);
  if (!start) return false;
  return start.getTime() <= Date.now();
}
function isBeforeScheduledPickup(it) {
  const start = parsePlanDateTime(it?.pickupDate || null, it?.pickupTime || null);
  if (!start) return true;
  return start.getTime() > Date.now();
}
function canCancelScheduledBooking(bk) {
  if (!bk || isRequestItem(bk) || isCancelledItem(bk) || hasBookingStarted(bk)) return false;
  return isBeforeScheduledPickup(bk);
}
function canCancelScheduledItem(it) {
  if (!it || isCancelledItem(it)) return false;
  return isRequestItem(it) ? isBeforeScheduledPickup(it) : canCancelScheduledBooking(it);
}
function canCancelScheduledPlan(g) {
  const activeItems = g.items.filter((it) => !isCancelledItem(it));
  if (!activeItems.length) return false;
  return activeItems.every((it) => canCancelScheduledItem(it));
}
function hasPlanStarted(g) {
  return g.items.some((bk) => !isRequestItem(bk) && hasBookingStarted(bk));
}
function planStartTimestamp(g) {
  const dt =
    parsePlanDateTime(g.pickupDate, g.pickupTime) || parsePlanDateTime(g.dropDate, g.dropTime);
  return dt ? dt.getTime() : 0;
}
function planEndTimestamp(g) {
  const dt =
    parsePlanDateTime(g.dropDate, g.dropTime) || parsePlanDateTime(g.pickupDate, g.pickupTime);
  return dt ? dt.getTime() : 0;
}
const bookedLike = (s) => {
  const k = String(s || "").toUpperCase();
  return k === "BOOKED" || k === "CONFIRMED";
};
const liveLike = (s) => {
  const k = String(s || "").toUpperCase();
  return k === "ARRIVED_AT_PICKUP" || k === "IN_PROGRESS" || k === "STARTED";
};
const completedLike = (s) => {
  const k = String(s || "").toUpperCase();
  return k === "COMPLETED" || k === "ENDED";
};
const terminalLike = (s) => {
  const k = String(s || "").toUpperCase();
  return (
    k === "CANCELLED" ||
    k === "EXPIRED" ||
    k === "UNFULFILLED" ||
    k === "COMPLETED" ||
    k === "ENDED"
  );
};

/**
 * ✅ FIX: For cancelled plans, show "Trip total" even if items are cancelled.
 * - Active plans: show total of active (non-cancelled) items
 * - Cancelled plans: show total of all items (including cancelled)
 * Also fix vehicle count chip similarly.
 */
function itemVehicleCount(it) {
  if (it?.__source === "scheduled_ops") {
    const accepted = Math.max(0, Number(it?.acceptedQuantity) || 0);
    const requested = Math.max(0, Number(it?.requestedQuantity) || 0);
    const remaining = Math.max(0, Number(it?.remainingQuantity) || 0);
    if (isRequestItem(it)) return Math.max(remaining, requested, 0);
    return Math.max(accepted, requested, 0);
  }
  return 1;
}

const totalVehicleCount = (g) =>
  g.items.reduce((acc, it) => acc + itemVehicleCount(it), 0);
const activeVehicleCount = (g) =>
  g.items.reduce((acc, it) => (isCancelledItem(it) ? acc : acc + itemVehicleCount(it)), 0);

const sumGroupFareAll = (g) => g.items.reduce((acc, it) => acc + (Number(it?.fare) || 0), 0);

const sumGroupFareActive = (g) =>
  g.items.reduce((acc, it) => (isCancelledItem(it) ? acc : acc + (Number(it?.fare) || 0)), 0);

function groupBookings(items) {
  const map = new Map();
  for (const it of items) {
    const planKey = normStr(it?.planKey);
    const key = planKey ? `PLAN:${planKey}` : makeTripKey(it);
    if (!map.has(key)) {
      map.set(key, {
        key,
        pickupLocation: normStr(it?.pickupLocation),
        destination: normStr(it?.destination || it?.finalDestination),
        stops: Array.isArray(it?.destinations)
          ? it.destinations.map((s) => normStr(s)).filter(Boolean)
          : [],
        pickupDate: it?.pickupDate || null,
        pickupTime: it?.pickupTime || null,
        dropDate: it?.dropDate || null,
        dropTime: it?.dropTime || null,
        items: [],
      });
    }
    const group = map.get(key);
    group.items.push(it);

    if (!group.pickupLocation) {
      group.pickupLocation = normStr(
        it?.pickupLocation || it?.pickupAddress || it?.fromAddress
      );
    }
    if (!group.destination) {
      group.destination = normStr(
        it?.destination ||
          it?.finalDestination ||
          it?.dropLocation ||
          it?.dropAddress ||
          it?.toAddress
      );
    }

    const nextStops = Array.isArray(it?.destinations)
      ? it.destinations.map((s) => normStr(s)).filter(Boolean)
      : Array.isArray(it?.stops)
      ? it.stops.map((s) => normStr(s)).filter(Boolean)
      : [];
    if (!group.stops.length && nextStops.length) {
      group.stops = nextStops;
    }

    if (!group.pickupDate && it?.pickupDate) group.pickupDate = it.pickupDate;
    if (!group.pickupTime && it?.pickupTime) group.pickupTime = it.pickupTime;
    if (!group.dropDate && it?.dropDate) group.dropDate = it.dropDate;
    if (!group.dropTime && it?.dropTime) group.dropTime = it.dropTime;
  }

  const groups = Array.from(map.values());

  groups.sort((a, b) => {
    const ba = groupBucket(a);
    const bb = groupBucket(b);
    if (ba !== bb) return ba - bb;

    if (ba === 0) {
      const sa = planStartTimestamp(a);
      const sb = planStartTimestamp(b);
      if (sa !== sb) return sa - sb;
    } else {
      const ea = planEndTimestamp(a);
      const eb = planEndTimestamp(b);
      if (ea !== eb) return eb - ea;
    }
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });

  return groups;
}

function sortTripGroups(groups) {
  return [...(Array.isArray(groups) ? groups : [])].sort((a, b) => {
    const ba = groupBucket(a);
    const bb = groupBucket(b);
    if (ba !== bb) return ba - bb;

    if (ba === 0) {
      const sa = planStartTimestamp(a);
      const sb = planStartTimestamp(b);
      if (sa !== sb) return sa - sb;
    } else {
      const ea = planEndTimestamp(a);
      const eb = planEndTimestamp(b);
      if (ea !== eb) return eb - ea;
    }

    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });
}

function groupBucket(g) {
  const scheduledGroup = isScheduledPlanGroup(g);
  const rideNowGroup = isRideNowGroup(g);
  const states = g.items.map((it) => getItemStatus(it));
  const allCancelled = states.every(
    (s) => s === "CANCELLED" || s === "EXPIRED" || s === "UNFULFILLED"
  );
  if (allCancelled) return 2;
  if (scheduledGroup && isPlanHistoryDue(g)) return 1;
  if (states.some((s) => s === "UNFULFILLED")) return 2;
  if (g.items.some((it) => isRequestItem(it))) return 0;
  if (states.some((s) => liveLike(s))) return 0;
  if (states.some((s) => bookedLike(s))) {
    return isPlanHistoryDue(g) ? 1 : 0;
  }
  if (states.some((s) => completedLike(s)) || isPlanPast(g)) {
    if (rideNowGroup && states.some((s) => completedLike(s))) return 1;
    return isPlanHistoryDue(g) ? 1 : 0;
  }
  return 0;
}

function groupStatusMeta(g) {
  const bucket = groupBucket(g);
  if (bucket === 2) return { label: "Cancelled", tone: "danger" };
  if (bucket === 1) return { label: "Completed", tone: "ink" };
  return { label: "Active", tone: "success" };
}
function groupStatusClass(tone) {
  if (tone === "danger") return "bg-rose-50 text-rose-700 border-rose-200";
  if (tone === "ink") return "bg-slate-900/5 text-slate-900 border-slate-300";
  return "bg-emerald-50 text-emerald-700 border-emerald-200";
}

function getItemVehicleId(it) {
  if (it?.vehicleId != null && String(it.vehicleId).trim()) return String(it.vehicleId).trim();

  const v = it?.vehicle;
  if (v?.registrationNumber) return String(v.registrationNumber).trim();
  if (v?.id != null && String(v.id).trim()) return String(v.id).trim();

  if (it?.vehicle_id != null && String(it.vehicle_id).trim()) return String(it.vehicle_id).trim();
  if (it?.vehicleRegistrationNumber) return String(it.vehicleRegistrationNumber).trim();

  return "";
}

function isAuthRequiredError(e) {
  if (!e) return false;
  if (e?.code === "AUTH_REQUIRED") return true;
  const s = e?.response?.status;
  if (s === 401 || s === 403) return true;
  const msg = String(e?.message || "").toLowerCase();
  return msg.includes("auth_required") || msg.includes("missing user identity");
}

const HISTORY_PAGE_SIZE = 10;
const HISTORY_TABS = ["completed", "cancelled"];

function createEmptyHistoryState() {
  return {
    groups: [],
    loading: false,
    loaded: false,
    hasMore: true,
    nextPage: 0,
  };
}

function debugPaidAmounts(label, payload) {
  try {
    console.groupCollapsed(`[MyBookings][PaidDebug] ${label}`);
    console.log(payload);
    console.groupEnd();
  } catch {
    console.log(`[MyBookings][PaidDebug] ${label}`, payload);
  }
}

function normalizeScheduledOpsRequests(trips) {
  const rows = [];
  for (const trip of Array.isArray(trips) ? trips : []) {
    const lineRows = Array.isArray(trip?.planSupplySummary?.lines)
      ? trip.planSupplySummary.lines
      : Array.isArray(trip?.planLines)
      ? trip.planLines
      : [];

    for (const [index, line] of lineRows.entries()) {
      const quantity = Math.max(0, Number(line?.quantity) || 0);
      const acceptedQuantity = Math.max(0, Number(line?.acceptedQuantity) || 0);
      const assignedVehicle = line?.assignedVehicle || {};
      const catalogVehicle = line?.catalogVehicle || {};
      const brand = String(
        assignedVehicle?.brand || catalogVehicle?.brand || line?.brand || ""
      ).trim();
      const model = String(
        assignedVehicle?.model || catalogVehicle?.model || line?.model || ""
      ).trim();
      const color = String(
        assignedVehicle?.color || catalogVehicle?.color || line?.color || ""
      ).trim();
      const registrationNumber = String(
        assignedVehicle?.registrationNumber || line?.registrationNumber || ""
      ).trim();
      const fare = Number(line?.lineTotalFare ?? line?.totalFare ?? line?.fare ?? 0) || 0;
      const rawCurrentPaidAmount = Number(line?.currentPaidAmount);
      const rawLinkedPaidAmount = Number(
        line?.linkedRequestPaidAmount ?? line?.bookingAmount ?? line?.lineBookingAmount
      );
      const rawSnapshotPaidAmount = Number(
        line?.snapshotPaidAmount ?? line?.bookingAmount ?? line?.lineBookingAmount
      );
      const paidNow = resolvePaidAmountLikeApp(null, {
        ...line,
        bookingAmount: line?.bookingAmount ?? line?.lineBookingAmount ?? 0,
        linkedRequestPaidAmount: line?.linkedRequestPaidAmount ?? line?.bookingAmount ?? line?.lineBookingAmount ?? 0,
      });
      const pending = resolvePendingAmountLikeApp(null, line, fare, paidNow);

      rows.push({
        __kind: acceptedQuantity > 0 ? "booking" : "request",
        __source: "scheduled_ops",
        id: line?.bookingId ?? line?.scheduledBookingId ?? null,
        requestId: line?.requestId ?? line?.scheduledRequestId ?? `${trip?.id || "trip"}-${index}`,
        cancelRequestId: pickScheduledRequestId(
          line?.requestId,
          line?.scheduledRequestId,
          line?.bookingMatchRequestId,
          line?.requestIds
        ),
        opsTripRequestId: trip?.id ?? null,
        status: String(line?.status || trip?.status || "BOOKED").toUpperCase(),
        rideStatus: String(line?.rideStatus || "").toUpperCase(),
        planKey: trip?.planKey || trip?.tripPlanKey || `SCHEDULED_${trip?.id || index}`,
        pickupLocation:
          trip?.pickupLocation || trip?.pickupAddress || trip?.fromAddress || "",
        destination:
          trip?.destination ||
          trip?.finalDestination ||
          trip?.dropLocation ||
          trip?.dropAddress ||
          trip?.toAddress ||
          "",
        destinations: Array.isArray(trip?.destinations)
          ? trip.destinations
          : Array.isArray(trip?.stops)
          ? trip.stops
          : [],
        pickupDate: trip?.pickupDate || trip?.date || null,
        pickupTime: trip?.pickupTime || trip?.time || null,
        dropDate: trip?.dropDate || trip?.pickupDate || trip?.date || null,
        dropTime: trip?.dropTime || trip?.pickupTime || trip?.time || null,
        fare,
        bookingAmount: paidNow,
        totalPaid: paidNow,
        currentPaidAmount: Number.isFinite(rawCurrentPaidAmount) ? rawCurrentPaidAmount : null,
        snapshotPaidAmount: Number.isFinite(rawSnapshotPaidAmount) ? rawSnapshotPaidAmount : null,
        linkedRequestPaidAmount: Number.isFinite(rawLinkedPaidAmount) ? rawLinkedPaidAmount : 0,
        pendingToDriver: pending,
        pendingCashAmount: pending,
        currentPendingAmount: Number.isFinite(Number(line?.currentPendingAmount))
          ? Number(line?.currentPendingAmount)
          : null,
        snapshotPendingCashAmount: Number.isFinite(Number(line?.snapshotPendingAmount))
          ? Number(line?.snapshotPendingAmount)
          : pending,
        snapshotPendingAmount: Number.isFinite(Number(line?.snapshotPendingAmount))
          ? Number(line?.snapshotPendingAmount)
          : null,
        financeSnapshot: trip?.financeSnapshot ?? trip?.planFinanceSnapshot ?? null,
        planLines: Array.isArray(trip?.planLines) ? trip.planLines : lineRows,
        planSupplySummary: trip?.planSupplySummary ?? null,
        opsCases: Array.isArray(trip?.cases) ? trip.cases : [],
        snapshotBookingAmount: Number.isFinite(rawSnapshotPaidAmount) ? rawSnapshotPaidAmount : 0,
        requestedQuantity: quantity,
        acceptedQuantity,
        remainingQuantity: Math.max(quantity - acceptedQuantity, 0),
        assignedDriverName: line?.assignedDriverName || trip?.assignedDriverName || "",
        assignedDriverPhoneNumber:
          line?.assignedDriverPhoneNumber || trip?.assignedDriverPhoneNumber || "",
        rideOtp: String(line?.rideOtp || trip?.rideOtp || "").trim(),
        vehicle: {
          id: assignedVehicle?.id || catalogVehicle?.id || line?.vehicleId || null,
          brand,
          model,
          color,
          registrationNumber,
          seatingCapacity:
            assignedVehicle?.seatingCapacity || catalogVehicle?.seatingCapacity || null,
          fuelType: assignedVehicle?.fuelType || catalogVehicle?.fuelType || "",
          type: assignedVehicle?.type || catalogVehicle?.type || "",
        },
      });
    }

    debugPaidAmounts("scheduled ops trip raw", {
      tripId: trip?.id ?? null,
      planKey: trip?.planKey || trip?.tripPlanKey || null,
      financeSnapshot: trip?.financeSnapshot || trip?.planFinanceSnapshot || null,
      lineRows,
    });
  }
  return rows;
}

function getEmbeddedVehicle(it) {
  return it?.vehicle ?? it?.vehicleSummary ?? it?.vehicleInfo ?? it?.vehicleDetails ?? null;
}

function getVehicleDisplayParts(it, vehicleMapArg = {}) {
  const vid = getItemVehicleId(it);
  const fetched = vid ? vehicleMapArg[vid] || {} : {};
  const embedded = getEmbeddedVehicle(it) || {};
  const merged = { ...embedded, ...fetched };
  const brand = String(
    merged?.brand || it?.brand || it?.catalogBrand || it?.vendorVehicleBrand || ""
  ).trim();
  const model = String(
    merged?.model || it?.model || it?.catalogModel || it?.vendorVehicleModel || ""
  ).trim();
  const color = String(
    merged?.color || it?.color || it?.catalogColor || it?.vendorVehicleColor || ""
  ).trim();
  const reg = String(
    merged?.registrationNumber ||
      it?.vehicleRegistrationNumber ||
      it?.assignedVehicleRegNo ||
      it?.vendorVehicleRegNo ||
      ""
  ).trim();
  const type = String(
    merged?.type || merged?.bodyType || it?.type || it?.catalogType || ""
  ).trim();
  return { merged, brand, model, color, reg, type };
}

function getVehicleTitle(it, vehicleMapArg = {}) {
  const { brand, model } = getVehicleDisplayParts(it, vehicleMapArg);
  return `${brand || "Vehicle"} ${model || ""}`.trim();
}

function getVehicleSubtitle(it, vehicleMapArg = {}) {
  const { color, reg, type } = getVehicleDisplayParts(it, vehicleMapArg);
  return [color, type, reg].filter(Boolean).join(" • ");
}

function getFinanceCaseForItem(it) {
  const financeSnapshot = it?.financeSnapshot;
  const financeCases = Array.isArray(financeSnapshot?.cases) ? financeSnapshot.cases : [];
  if (!financeCases.length) return null;

  const requestId = it?.requestId ?? it?.bookingMatchRequestId ?? null;
  if (requestId != null) {
    const byRequestId = financeCases.find(
      (row) => String(row?.requestId ?? "") === String(requestId)
    );
    if (byRequestId) return byRequestId;
  }

  const caseId = it?.opsTripCaseId ?? it?.caseId ?? null;
  if (caseId != null) {
    const byCaseId = financeCases.find((row) => String(row?.caseId ?? "") === String(caseId));
    if (byCaseId) return byCaseId;
  }

  const bookingId = it?.id ?? it?.finalBookingId ?? null;
  if (bookingId != null) {
    const byBookingId = financeCases.find(
      (row) => String(row?.bookingId ?? "") === String(bookingId)
    );
    if (byBookingId) return byBookingId;
  }

  return null;
}

function resolvePaidAmountLikeApp(financeCase, row) {
  const currentPaidAmount = Number(financeCase?.currentPaidAmount ?? row?.currentPaidAmount);
  const linkedPaidAmount = Number(row?.linkedRequestPaidAmount);
  const snapshotPaidAmount = Number(
    financeCase?.snapshotPaidAmount ??
      row?.snapshotPaidAmount ??
      row?.bookingAmount ??
      row?.lineBookingAmount
  );

  if (Number.isFinite(currentPaidAmount) && currentPaidAmount > 0) {
    return Math.max(0, currentPaidAmount);
  }
  if (Number.isFinite(linkedPaidAmount) && linkedPaidAmount > 0) {
    return Math.max(0, linkedPaidAmount);
  }
  if (Number.isFinite(snapshotPaidAmount)) {
    return Math.max(0, snapshotPaidAmount);
  }
  return 0;
}

function resolvePendingAmountLikeApp(financeCase, row, fareAmount, paidAmount) {
  const currentPendingAmount = Number(financeCase?.currentPendingAmount ?? row?.currentPendingAmount);
  const snapshotPendingAmount = Number(
    financeCase?.snapshotPendingAmount ??
      row?.snapshotPendingAmount ??
      row?.pendingCashAmount ??
      row?.pendingToDriver
  );

  if (Number.isFinite(currentPendingAmount) && currentPendingAmount >= 0) {
    return currentPendingAmount;
  }
  if (Number.isFinite(snapshotPendingAmount) && snapshotPendingAmount >= 0) {
    return snapshotPendingAmount;
  }
  return Math.max(0, Number(fareAmount || 0) - Number(paidAmount || 0));
}

function itemFare(it) {
  const financeCase = getFinanceCaseForItem(it);
  const financeFare = Number(financeCase?.snapshotFareAmount);
  if (Number.isFinite(financeFare) && financeFare > 0) return financeFare;
  return n(it?.fare);
}

function itemPaidNow(it) {
  const financeCase = getFinanceCaseForItem(it);
  return resolvePaidAmountLikeApp(financeCase, {
    ...it,
    bookingAmount: it?.bookingAmount ?? it?.totalPaid ?? it?.paidNow,
  });
}

function itemPendingToDriver(it) {
  const financeCase = getFinanceCaseForItem(it);
  return resolvePendingAmountLikeApp(financeCase, it, itemFare(it), itemPaidNow(it));
}

function refundStateOf(itemLike) {
  const raw = String(itemLike?.refundStatus || itemLike?.razorpayRefundStatus || "")
    .trim()
    .toUpperCase();
  if (raw === "PENDING" || raw === "COMPLETED" || raw === "FAILED") return raw;
  const pending = Number(itemLike?.pendingRefundAmount || 0);
  const refunded = Number(itemLike?.refundedAmount || 0);
  if (pending > 0) return "PENDING";
  if (refunded > 0) return "COMPLETED";
  return "NONE";
}

function refundAmountOf(itemLike) {
  const pending = Number(itemLike?.pendingRefundAmount || 0);
  const refunded = Number(itemLike?.refundedAmount || 0);
  return pending > 0 ? pending : refunded > 0 ? refunded : 0;
}

function mergeRefundState(current, next) {
  const rank = { NONE: 0, FAILED: 1, COMPLETED: 2, PENDING: 3 };
  const currentRank = rank[String(current || "NONE").toUpperCase()] ?? 0;
  const nextRank = rank[String(next || "NONE").toUpperCase()] ?? 0;
  return nextRank > currentRank ? next : current;
}

function refundTone(state) {
  const s = String(state || "").toUpperCase();
  if (s === "COMPLETED") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (s === "PENDING") return "border-amber-200 bg-amber-50 text-amber-700";
  if (s === "FAILED") return "border-rose-200 bg-rose-50 text-rose-700";
  return "border-slate-200 bg-slate-50 text-slate-600";
}

function groupBookingVehiclesByModel(g, vehicleMapArg = {}) {
  const financeSnapshot = g.items.find((it) => it?.financeSnapshot)?.financeSnapshot || null;
  const financeGroups = Array.isArray(financeSnapshot?.groupedVehicles)
    ? financeSnapshot.groupedVehicles
    : [];
  const financeCases = Array.isArray(financeSnapshot?.cases) ? financeSnapshot.cases : [];
  const requestOnly = g.items.length > 0 && g.items.every((it) => isRequestItem(it));
  const hasMeaningfulFinanceTitles = financeGroups.some((row) => {
    const title = String(row?.title || "").trim().toLowerCase();
    return title.length > 0 && title !== "vehicle";
  });

  if (financeGroups.length && requestOnly && hasMeaningfulFinanceTitles) {
    return financeGroups.map((row, index) => ({
      key: `${String(row?.title || "vehicle").toLowerCase()}|${String(
        row?.subtitle || ""
      ).toLowerCase()}|${index}`,
      title: String(row?.title || "Vehicle").trim() || "Vehicle",
      subtitle: String(row?.subtitle || "").trim(),
      quantity: Math.max(1, Number(row?.quantity) || 1),
      totalFare: Number(row?.snapshotFareAmount) || 0,
      totalPaid: resolvePaidAmountLikeApp(null, row),
      totalPending: resolvePendingAmountLikeApp(
        null,
        row,
        Number(row?.snapshotFareAmount) || 0,
        resolvePaidAmountLikeApp(null, row)
      ),
      eachDriverPending: (() => {
        const current = Number(row?.perCarCurrentPendingAmount);
        const snapshot = Number(row?.perCarSnapshotPendingAmount);
        const fare = Number(row?.perCarSnapshotFareAmount ?? row?.snapshotFareAmount) || 0;
        const paid = resolvePaidAmountLikeApp(null, {
          currentPaidAmount: row?.perCarCurrentPaidAmount,
          snapshotPaidAmount: row?.perCarSnapshotPaidAmount,
        });
        return Number.isFinite(current) && current >= 0
          ? current
          : Number.isFinite(snapshot) && snapshot >= 0
          ? snapshot
          : Math.max(0, fare - paid);
      })(),
      eachDriverPaid: resolvePaidAmountLikeApp(null, {
        currentPaidAmount: row?.perCarCurrentPaidAmount,
        snapshotPaidAmount: row?.perCarSnapshotPaidAmount,
      }),
      eachDriverFare:
        Number(row?.perCarSnapshotFareAmount ?? row?.snapshotFareAmount) || 0,
      mixedPending: false,
    }));
  }

  if (financeCases.length && requestOnly) {
    const groupedFromCases = new Map();
    for (const row of financeCases) {
      const brand = String(row?.brand || "").trim();
      const model = String(row?.model || "").trim();
      const color = String(row?.color || "").trim();
      const vehicleType = String(row?.vehicleType || "").trim();
      const title = `${brand || "Vehicle"} ${model || ""}`.trim() || "Vehicle";
      const subtitle = [color, vehicleType].filter(Boolean).join(" • ");
      const key = `${title.toLowerCase()}|${subtitle.toLowerCase()}`;
      const fare = Number(row?.snapshotFareAmount) || 0;
      const paid = resolvePaidAmountLikeApp(null, row);
      const pending = resolvePendingAmountLikeApp(null, row, fare, paid);

      const prev = groupedFromCases.get(key);
	      if (prev) {
	        prev.quantity += 1;
	        prev.totalFare += fare;
	        prev.totalPaid += paid;
	        prev.totalPending += pending;
	        prev.totalRefundAmount += refundAmountOf(row);
	        prev.refundState = mergeRefundState(prev.refundState, refundStateOf(row));
	        if (prev.eachDriverPending != null && Math.abs(prev.eachDriverPending - pending) > 0.01) {
	          prev.mixedPending = true;
	        }
	        continue;
	      }

	      groupedFromCases.set(key, {
	        key,
	        title,
	        subtitle,
	        quantity: 1,
	        totalFare: fare,
	        totalPaid: paid,
	        totalPending: pending,
	        totalRefundAmount: refundAmountOf(row),
	        refundState: refundStateOf(row),
	        eachDriverPending: pending,
	        eachDriverPaid: paid,
	        eachDriverFare: fare,
	        mixedPending: false,
	      });
    }
    return Array.from(groupedFromCases.values()).sort((a, b) => a.title.localeCompare(b.title));
  }

  const grouped = new Map();

  for (const it of g.items) {
    if (isCancelledItem(it)) continue;
    const title = getVehicleTitle(it, vehicleMapArg) || "Vehicle";
    const subtitle = getVehicleSubtitle(it, vehicleMapArg);
    const key = `${title.toLowerCase()}|${subtitle.toLowerCase()}`;
    const quantity = Math.max(1, itemVehicleCount(it));
    const fare = itemFare(it);
    const paid = itemPaidNow(it);
    const pending = itemPendingToDriver(it);
    const eachDriverFare = fare / quantity;
    const eachDriverPaid = paid / quantity;
    const eachDriverPending = pending / quantity;

	    const current = grouped.get(key) || {
	      key,
	      title,
	      subtitle,
	      quantity: 0,
	      totalFare: 0,
	      totalPaid: 0,
	      totalPending: 0,
	      totalRefundAmount: 0,
	      refundState: "NONE",
	      eachDriverFare: null,
	      eachDriverPaid: null,
	      eachDriverPending: null,
	    };

	    current.quantity += quantity;
	    current.totalFare += fare;
	    current.totalPaid += paid;
	    current.totalPending += pending;
	    current.totalRefundAmount += refundAmountOf(it);
	    current.refundState = mergeRefundState(current.refundState, refundStateOf(it));
	    current.eachDriverFare =
      current.eachDriverFare == null ? eachDriverFare : Math.min(current.eachDriverFare, eachDriverFare);
    current.eachDriverPaid =
      current.eachDriverPaid == null ? eachDriverPaid : Math.min(current.eachDriverPaid, eachDriverPaid);
    current.eachDriverPending =
      current.eachDriverPending == null
        ? eachDriverPending
        : Math.min(current.eachDriverPending, eachDriverPending);
    grouped.set(key, current);
  }

  const groupedRows = Array.from(grouped.values()).sort((a, b) => a.title.localeCompare(b.title));
  debugPaidAmounts("groupBookingVehiclesByModel derived", {
    groupKey: g?.key,
    pickup: g?.pickupLocation || null,
    destination: g?.destination || null,
    financeSnapshot,
    rawItems: g.items.map((it) => ({
      id: it?.id ?? null,
      requestId: it?.requestId ?? null,
      source: it?.__source ?? null,
      status: it?.status ?? null,
      fare: it?.fare ?? null,
      bookingAmount: it?.bookingAmount ?? null,
      currentPaidAmount: it?.currentPaidAmount ?? null,
      snapshotPaidAmount: it?.snapshotPaidAmount ?? null,
      pendingCashAmount: it?.pendingCashAmount ?? null,
      snapshotPendingCashAmount: it?.snapshotPendingCashAmount ?? null,
      financeCase: getFinanceCaseForItem(it),
    })),
    groupedRows,
  });
  return groupedRows;
}

function getGroupSummaryAmounts(g) {
  const financeSnapshot = g.items.find((it) => it?.financeSnapshot)?.financeSnapshot || null;
  const hasScheduledOpsItems = g.items.some((it) => it?.__source === "scheduled_ops");
  const requestOnly = g.items.length > 0 && g.items.every((it) => isRequestItem(it));

  if (financeSnapshot && requestOnly && hasScheduledOpsItems) {
    const financeCases = Array.isArray(financeSnapshot?.cases) ? financeSnapshot.cases : [];
    if (financeCases.length) {
      const total = financeCases.reduce(
        (acc, row) => acc + (Number(row?.snapshotFareAmount) || 0),
        0
      );
      const paid = financeCases.reduce(
        (acc, row) => acc + resolvePaidAmountLikeApp(null, row),
        0
      );
      const pending = financeCases.reduce(
        (acc, row) =>
          acc +
          resolvePendingAmountLikeApp(
            null,
            row,
            Number(row?.snapshotFareAmount) || 0,
            resolvePaidAmountLikeApp(null, row)
          ),
        0
      );
      const summary = { total, paid, pending };
      debugPaidAmounts("plan summary from finance cases", {
        groupKey: g?.key,
        financeCases,
        summary,
      });
      return summary;
    }

    const snapshotTotal = Number(financeSnapshot?.snapshotTotalFareAmount) || 0;
    const snapshotPaid = Number(financeSnapshot?.snapshotTotalPaidAmount) || 0;
    const currentPaid = Number(financeSnapshot?.currentTotalPaidAmount);
    const resolvedPaid =
      Number.isFinite(currentPaid) && currentPaid > 0 ? currentPaid : snapshotPaid;
    const currentPending = Number(financeSnapshot?.currentTotalPendingAmount);
    const resolvedPending =
      Number.isFinite(currentPending) && currentPending >= 0
        ? currentPending
        : Math.max(0, snapshotTotal - resolvedPaid);
    const summary = {
      total: snapshotTotal,
      paid: resolvedPaid,
      pending: resolvedPending,
    };
    debugPaidAmounts("plan summary from finance snapshot", {
      groupKey: g?.key,
      financeSnapshot,
      summary,
    });
    return summary;
  }

  const allCancelled = g.items.every((it) => isCancelledItem(it));
  const summary = {
    total: allCancelled ? sumGroupFareAll(g) : sumGroupFareActive(g),
    paid: g.items.reduce(
      (acc, it) => (allCancelled || !isCancelledItem(it) ? acc + itemPaidNow(it) : acc),
      0
    ),
    pending: g.items.reduce(
      (acc, it) =>
        allCancelled || !isCancelledItem(it) ? acc + itemPendingToDriver(it) : acc,
      0
    ),
  };
  debugPaidAmounts("plan summary fallback", {
    groupKey: g?.key,
    allCancelled,
    rawItems: g.items.map((it) => ({
      id: it?.id ?? null,
      requestId: it?.requestId ?? null,
      source: it?.__source ?? null,
      status: it?.status ?? null,
      fare: itemFare(it),
      paid: itemPaidNow(it),
      pending: itemPendingToDriver(it),
      bookingAmount: it?.bookingAmount ?? null,
      currentPaidAmount: it?.currentPaidAmount ?? null,
      snapshotPaidAmount: it?.snapshotPaidAmount ?? null,
      pendingCashAmount: it?.pendingCashAmount ?? null,
      snapshotPendingCashAmount: it?.snapshotPendingCashAmount ?? null,
      financeCase: getFinanceCaseForItem(it),
    })),
    summary,
  });
  return summary;
}

const DEFAULT_CONTACT_DETAILS_VISIBLE_BEFORE_PICKUP_HOURS = 24;

function RouteInfoCard({ icon, label, title, meta, accent = "slate" }) {
  const accentClasses =
    accent === "emerald"
      ? "border-emerald-200/80 bg-[linear-gradient(180deg,rgba(236,253,245,0.95),rgba(255,255,255,0.98))] text-emerald-700 shadow-[0_10px_24px_rgba(16,185,129,0.08)]"
      : "border-slate-200 bg-[linear-gradient(180deg,rgba(248,250,252,0.96),rgba(255,255,255,0.98))] text-slate-600 shadow-[0_10px_24px_rgba(15,23,42,0.05)]";

  return (
    <div className="rounded-[22px] border border-slate-200/90 bg-white p-5 shadow-[0_18px_40px_rgba(15,23,42,0.06)]">
      <div className="flex items-start gap-3">
        <div
          className={[
            "flex h-12 w-12 shrink-0 items-center justify-center rounded-[16px] border",
            accentClasses,
          ].join(" ")}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
            {label}
          </div>
          <div className="mt-2 text-[16px] font-semibold leading-7 text-slate-900">{title}</div>
          <div className="mt-3 inline-flex rounded-[12px] bg-slate-100 px-3 py-1 text-[12px] font-medium text-slate-600">
            {meta}
          </div>
        </div>
      </div>
    </div>
  );
}

function PickupIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 21s6-5.1 6-11a6 6 0 1 0-12 0c0 5.9 6 11 6 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

function DropIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 21s6-5.1 6-11a6 6 0 1 0-12 0c0 5.9 6 11 6 11Z" />
      <path d="M9.5 10.5 11 12l3.5-3.5" />
    </svg>
  );
}

function getScheduledContactVisibilityHours(input) {
  const hours = Number(input);
  if (!Number.isFinite(hours) || hours < 0) return DEFAULT_CONTACT_DETAILS_VISIBLE_BEFORE_PICKUP_HOURS;
  return Math.max(0, Math.round(hours));
}

function areScheduledContactDetailsVisible(g, visibleBeforePickupHours, nowMs = Date.now()) {
  const pickupDt = parsePlanDateTime(g.pickupDate || null, g.pickupTime || null);
  if (!pickupDt) return false;
  const visibleAtMs = pickupDt.getTime() - visibleBeforePickupHours * 60 * 60 * 1000;
  return nowMs >= visibleAtMs;
}

function scheduledContactVisibilityMessage(visibleBeforePickupHours) {
  const hours = getScheduledContactVisibilityHours(visibleBeforePickupHours);
  return `Contact details will be visible ${hours} ${hours === 1 ? "hour" : "hours"} before pickup time.`;
}

function getDisplayPlanKey(g, fallbackIndex = 0) {
  const raw = String(
    g?.items?.find((it) => String(it?.planKey || "").trim())?.planKey || ""
  ).trim();
  return raw || `Plan ${fallbackIndex + 1}`;
}

function MobileMetaIcon({ type }) {
  if (type === "time") {
    return (
      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.9">
        <circle cx="12" cy="12" r="8" />
        <path d="M12 7.8v4.6l3 1.8" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.9">
      <rect x="4" y="5" width="16" height="15" rx="2.5" />
      <path d="M8 3.5v3M16 3.5v3M4 9.5h16" />
    </svg>
  );
}

function MobileStageCard({ icon, label, location, date, time }) {
  return (
    <div className="flex-1 rounded-[16px] border border-slate-200 bg-white px-3 py-3 shadow-sm">
      <div className="flex items-center gap-2 text-slate-500">
        <div className="text-slate-500">{icon}</div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em]">{label}</div>
      </div>
      <div className="mt-2 min-h-[38px] text-[14px] font-semibold leading-5 text-slate-900">
        {location || "—"}
      </div>
      <div className="mt-3 rounded-[12px] border border-slate-200 bg-slate-50/80 px-3 py-2.5">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-700">
          <MobileMetaIcon type="date" />
          <span className="truncate">{date || "N/A"}</span>
        </div>
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-slate-700">
          <MobileMetaIcon type="time" />
          <span className="truncate">{time || "N/A"}</span>
        </div>
      </div>
    </div>
  );
}

function MobileVehicleSummaryCard({ summary, canReview = false, onReview = null }) {
  const perCarFare = summary.eachDriverFare ?? summary.totalFare / Math.max(1, summary.quantity);
  const perCarPaid = summary.eachDriverPaid ?? summary.totalPaid / Math.max(1, summary.quantity);
  const perCarPending =
    summary.eachDriverPending ?? summary.totalPending / Math.max(1, summary.quantity);
  const refundState = String(summary.refundState || "NONE").toUpperCase();
  const refundAmount = Number(summary.totalRefundAmount || 0);
  return (
    <div className="rounded-[18px] border border-slate-200 bg-white p-3.5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-semibold leading-5 text-slate-900">{summary.title}</div>
          {summary.subtitle ? (
            <div className="mt-1 text-[11px] leading-4 text-slate-500">{summary.subtitle}</div>
          ) : null}
          {refundState !== "NONE" ? (
            <div
              className={[
                "mt-2 inline-flex items-center rounded-[10px] border px-2.5 py-1 text-[11px] font-semibold",
                refundTone(refundState),
              ].join(" ")}
            >
              {refundState === "COMPLETED"
                ? `Refund completed ${fmtINR(refundAmount)}`
                : refundState === "PENDING"
                ? "Refund in process"
                : "Refund update failed"}
            </div>
          ) : null}
        </div>
        <div className="rounded-[10px] bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-800">
          {summary.quantity} {summary.quantity === 1 ? "car" : "cars"}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-[14px] border border-slate-200 bg-slate-50/60 px-3 py-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
            Per car total cost
          </div>
          <div className="mt-1 text-[14px] font-bold text-slate-900">{fmtINR(perCarFare)}</div>
          <div className="mt-1 text-[11px] text-slate-500">Per vehicle cost</div>
        </div>
        <div className="rounded-[14px] border border-slate-200 bg-slate-50/60 px-3 py-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
            Per car paid already
          </div>
          <div className="mt-1 text-[14px] font-bold text-slate-900">{fmtINR(perCarPaid)}</div>
          <div className="mt-1 text-[11px] text-slate-500">Upfront paid</div>
        </div>
      </div>

      <div className="mt-2 rounded-[14px] border border-emerald-200 bg-emerald-50/70 px-3 py-3">
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
          Per car pay in cash to driver
        </div>
        <div className="mt-1 text-[16px] font-bold text-emerald-700">{fmtINR(perCarPending)}</div>
      </div>

      {summary.quantity > 1 ? (
        <div className="mt-2 rounded-[14px] border border-slate-200 bg-white px-3 py-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
            Calculation
          </div>
          <div className="mt-1 text-[13px] font-bold text-slate-900">
            {`${fmtINR(perCarFare)} × ${summary.quantity} = ${fmtINR(summary.totalFare)}`}
          </div>
          <div className="mt-1 text-[12px] text-slate-600">
            {`${fmtINR(perCarPaid)} × ${summary.quantity} = ${fmtINR(summary.totalPaid)}`}
          </div>
          <div className="mt-1 text-[12px] font-semibold text-emerald-700">
            {`${fmtINR(perCarPending)} × ${summary.quantity} = ${fmtINR(summary.totalPending)}`}
          </div>
        </div>
      ) : null}

      {canReview && onReview ? (
        <button
          type="button"
          onClick={onReview}
          className="mt-3 inline-flex min-h-[40px] items-center gap-1.5 rounded-[12px] border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800"
        >
          <StarOutlineIcon className="h-3.5 w-3.5" />
          <span>Review plan</span>
        </button>
      ) : null}
    </div>
  );
}

function buildScheduledPlanSuppliers(g, vehicleMapArg = {}) {
  const suppliers = new Map();
  for (const item of g.items) {
    const supplierName = String(
      item?.vendorName || item?.assignedDriverName || ""
    ).trim();
    const supplierPhone = String(
      item?.vendorPhone || item?.assignedDriverPhoneNumber || ""
    ).trim();
    if (!supplierName && !supplierPhone) continue;
    const title = getVehicleTitle(item, vehicleMapArg) || "Vehicle";
    const quantity = Math.max(1, itemVehicleCount(item));
    const key = `${supplierName}|${supplierPhone}`;
    const current = suppliers.get(key) || {
      key,
      supplierName: supplierName || "Supplier",
      supplierPhone,
      items: [],
    };
    const existing = current.items.find((entry) => entry.title === title);
    if (existing) existing.quantity += quantity;
    else current.items.push({ title, quantity });
    suppliers.set(key, current);
  }
  return Array.from(suppliers.values()).sort((a, b) =>
    a.supplierName.localeCompare(b.supplierName)
  );
}

function getScheduledPlanAlignmentSummary(g) {
  let requested = 0;
  let accepted = 0;
  for (const item of g.items) {
    if (item?.__source !== "scheduled_ops") continue;
    requested += Math.max(0, Number(item?.requestedQuantity) || 0);
    accepted += Math.max(0, Number(item?.acceptedQuantity) || 0);
  }
  return { requested, accepted, pending: Math.max(0, requested - accepted) };
}

/* ───────── UI rules (Request status + Reg no.) ───────── */
const MS_6H = 6 * 60 * 60 * 1000;

function shouldShowRegistrationNumber(it) {
  if (!it || isRequestItem(it)) return false;
  if (isScheduledBookingItem(it)) return false;
  const st = getItemStatus(it);
  if (terminalLike(st)) return false;

  const pickupDt = parsePlanDateTime(it?.pickupDate || null, it?.pickupTime || null);
  if (!pickupDt) return bookedLike(st) || liveLike(st);

  return pickupDt.getTime() - Date.now() <= MS_6H;
}

/* ───────── Page ───────── */
export default function MyBookings() {
  const { user } = useUser();
  const navigate = useNavigate();

  const [activeGroups, setActiveGroups] = useState([]);
  const [historyState, setHistoryState] = useState({
    completed: createEmptyHistoryState(),
    cancelled: createEmptyHistoryState(),
  });
  const [currentTab, setCurrentTab] = useState("active");
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  const [resolvedUserId, setResolvedUserId] = useState(null);
  const [vehicleMap, setVehicleMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [cancelSuccessMessage, setCancelSuccessMessage] = useState("");

  const [planToCancel, setPlanToCancel] = useState(null);
  const [planCancelBusy, setPlanCancelBusy] = useState(false);
  const [supplierModal, setSupplierModal] = useState({ open: false, group: null });
  const [stopsModal, setStopsModal] = useState({ open: false, group: null });
  const [scheduledContactVisibleBeforePickupHours, setScheduledContactVisibleBeforePickupHours] =
    useState(DEFAULT_CONTACT_DETAILS_VISIBLE_BEFORE_PICKUP_HOURS);

  const hydrateVehicleDetails = React.useCallback(async (groupsToHydrate) => {
    const items = (Array.isArray(groupsToHydrate) ? groupsToHydrate : []).flatMap((g) =>
      Array.isArray(g?.items) ? g.items : []
    );
    const fetched = Object.create(null);

    await Promise.all(
      items.map(async (it) => {
        const id = getItemVehicleId(it);
        if (!id || fetched[id]) return;
        fetched[id] = true;
        try {
          const v = await getVehicleById(id);
          if (!v) return;
          setVehicleMap((prev) => (prev[id] ? prev : { ...prev, [id]: v }));
        } catch (e) {
          console.error("Vehicle fetch failed:", id, e);
        }
      })
    );
  }, []);

  const resolveEffectiveUserId = React.useCallback(async () => {
    if (resolvedUserId != null) return resolvedUserId;
    const nextUserId = await resolveUserIdForAuthenticatedUser(user ?? null);
    if (nextUserId != null) setResolvedUserId(nextUserId);
    return nextUserId;
  }, [resolvedUserId, user]);

  const loadActiveGroups = React.useCallback(async () => {
    try {
      setLoading(true);
      const userId = await resolveEffectiveUserId();
      if (!userId) {
        const err = new Error("Need userId to load bookings.");
        err.code = "AUTH_REQUIRED";
        throw err;
      }

      const [payload, activeOpsTrips] = await Promise.all([
        getUserBookingActiveGroupsByUserId(userId),
        listActiveUserOpsTripRequests(userId).catch(() => []),
      ]);
      debugPaidAmounts("active bookings API payload", {
        userId,
        payload,
        activeOpsTrips,
      });

      const backendGroups = Array.isArray(payload?.groups) ? payload.groups : [];
      let scheduledGroups = [];
      try {
        scheduledGroups = sortTripGroups(
          groupBookings(
            normalizeScheduledOpsRequests(Array.isArray(activeOpsTrips) ? activeOpsTrips : [])
          ).filter((group) => isScheduledOpsGroup(group) && groupBucket(group) === 0)
        );
      } catch (opsError) {
        console.warn("[MyBookings] failed to normalize scheduled ops groups", {
          userId,
          opsError,
          activeOpsTrips,
        });
      }
      const nextGroups = sortTripGroups([
        ...backendGroups.filter((group) => !isScheduledOpsGroup(group)),
        ...scheduledGroups,
      ]);
      debugPaidAmounts("active bookings merged groups", {
        userId,
        groups: nextGroups,
      });

      setActiveGroups(nextGroups);
      await hydrateVehicleDetails(nextGroups);
    } catch (e) {
      console.error("Error loading active booking groups", e);
      if (isAuthRequiredError(e)) {
        navigate("/signin-required", { replace: true, state: { from: "/my-bookings" } });
        return;
      }
      setActiveGroups([]);
    } finally {
      setLoading(false);
    }
  }, [hydrateVehicleDetails, navigate, resolveEffectiveUserId]);

  const loadHistoryPage = React.useCallback(
    async (tab, options = {}) => {
      const safeTab = tab === "cancelled" ? "cancelled" : "completed";
      const current = historyState[safeTab];
      if (current.loading) return;
      if (!options.reset && current.loaded && !current.hasMore) return;

      const targetPage = options.reset ? 0 : current.nextPage;
      setHistoryState((prev) => ({
        ...prev,
        [safeTab]: {
          ...(options.reset ? createEmptyHistoryState() : prev[safeTab]),
          loading: true,
        },
      }));

      try {
        const userId = await resolveEffectiveUserId();
        if (!userId) {
          const err = new Error("Need userId to load booking history.");
          err.code = "AUTH_REQUIRED";
          throw err;
        }

        const payload = await getUserBookingHistoryGroupsByUserId(
          userId,
          safeTab,
          targetPage,
          HISTORY_PAGE_SIZE
        );
        debugPaidAmounts("history bookings API payload", {
          userId,
          tab: safeTab,
          page: targetPage,
          payload,
        });
        const nextGroups = Array.isArray(payload?.groups) ? payload.groups : [];

        setHistoryState((prev) => ({
          ...prev,
          [safeTab]: {
            groups: options.reset ? nextGroups : [...prev[safeTab].groups, ...nextGroups],
            loading: false,
            loaded: true,
            hasMore: Boolean(payload?.hasMore),
            nextPage: Number.isFinite(Number(payload?.nextPage))
              ? Number(payload.nextPage)
              : targetPage + 1,
          },
        }));
        await hydrateVehicleDetails(nextGroups);
      } catch (e) {
        console.error(`Error loading ${safeTab} booking history`, e);
        if (isAuthRequiredError(e)) {
          navigate("/signin-required", { replace: true, state: { from: "/my-bookings" } });
          return;
        }
        setHistoryState((prev) => ({
          ...prev,
          [safeTab]: {
            ...prev[safeTab],
            loading: false,
            loaded: true,
            hasMore: false,
          },
        }));
      }
    },
    [historyState, hydrateVehicleDetails, navigate, resolveEffectiveUserId]
  );

  const loadAllHistory = React.useCallback(
    async (options = {}) => {
      for (const tab of HISTORY_TABS) {
        await loadHistoryPage(tab, options);
      }
    },
    [loadHistoryPage]
  );

  useEffect(() => {
    loadActiveGroups().catch(() => undefined);
  }, [loadActiveGroups]);

  useEffect(() => {
    let active = true;
    getSearchResultsConfig()
      .then((config) => {
        if (!active) return;
        setScheduledContactVisibleBeforePickupHours(
          getScheduledContactVisibilityHours(
            config?.scheduleContactDetailsVisibleBeforePickupHours
          )
        );
      })
      .catch(() => {
        if (!active) return;
        setScheduledContactVisibleBeforePickupHours(
          DEFAULT_CONTACT_DETAILS_VISIBLE_BEFORE_PICKUP_HOURS
        );
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (currentTab === "history") {
      const needsHistory =
        !historyState.completed.loaded || !historyState.cancelled.loaded;
      if (needsHistory) {
        loadAllHistory({ reset: true }).catch(() => undefined);
      }
    }
  }, [currentTab, historyState.cancelled.loaded, historyState.completed.loaded, loadAllHistory]);

  useEffect(() => {
    if (!mobileHistoryOpen) return;
    const needsHistory =
      !historyState.completed.loaded ||
      !historyState.cancelled.loaded;
    if (needsHistory) {
      loadAllHistory({ reset: true }).catch(() => undefined);
    }
  }, [historyState.cancelled.loaded, historyState.completed.loaded, loadAllHistory, mobileHistoryOpen]);

  useEffect(() => {
    if (!cancelSuccessMessage) return;
    const timer = window.setTimeout(() => setCancelSuccessMessage(""), 2400);
    return () => window.clearTimeout(timer);
  }, [cancelSuccessMessage]);

  const groups = useMemo(() => {
    if (currentTab === "history") {
      return sortTripGroups([
        ...historyState.completed.groups,
        ...historyState.cancelled.groups,
      ]);
    }
    return activeGroups;
  }, [activeGroups, currentTab, historyState]);

  const mobileHistoryGroups = useMemo(
    () =>
      sortTripGroups([
        ...historyState.completed.groups,
        ...historyState.cancelled.groups,
      ]),
    [historyState]
  );
  const mobileHistoryPending =
    mobileHistoryOpen &&
    (!historyState.completed.loaded ||
      !historyState.cancelled.loaded ||
      historyState.completed.loading ||
      historyState.cancelled.loading);

  const openCancelPlan = (g) => {
    setPlanToCancel(g);
    setPlanCancelBusy(false);
  };

  // ✅ Plan cancel cancels BOTH:
  // - final bookings (if not started)
  // - pending requests (if not cancelled)
  const actuallyCancelPlan = async () => {
    if (!planToCancel) return;
    setPlanCancelBusy(true);

    try {
      const planKey = String(
        planToCancel.items.find((it) => String(it?.planKey || "").trim())?.planKey || ""
      ).trim();
      if (!planKey) throw new Error("Missing plan key for this scheduled plan.");
      await cancelScheduledPlanForUser(planKey, user ?? null);
      await loadActiveGroups();
      if (historyState.cancelled.loaded || historyState.completed.loaded) {
        await loadAllHistory({ reset: true });
      }
      setCancelSuccessMessage("Trip plan cancelled successfully.");
      setPlanToCancel(null);
    } catch (e) {
      console.error("Plan cancel failed", e);
      if (isAuthRequiredError(e)) {
        navigate("/signin-required", { replace: true, state: { from: "/my-bookings" } });
        return;
      }
      alert("Failed to cancel plan. Please try again.");
      setPlanCancelBusy(false);
    }
  };

  const handleReviewPlan = (g) => {
    const firstFinal = g.items.find((it) => !isRequestItem(it));
    const rawPlanKey = firstFinal?.planKey;
    const hasBackendKey = typeof rawPlanKey === "string" && rawPlanKey.trim().length > 0;

    const params = new URLSearchParams();
    params.set("groupKey", g.key);
    if (hasBackendKey) params.set("planKey", rawPlanKey.trim());

    navigate(`/reviews/plan?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />

      <main className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6 sm:py-8 xl:px-10 md:py-10">
        {cancelSuccessMessage ? (
          <div className="mb-4 rounded-[18px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 shadow-sm">
            {cancelSuccessMessage}
          </div>
        ) : null}
        <section className="sm:hidden">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-[18px] font-semibold tracking-tight text-slate-900">
                My Bookings
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setMobileHistoryOpen(true);
                  if (!historyState.completed.loaded || !historyState.cancelled.loaded) {
                    loadAllHistory({ reset: true }).catch(() => undefined);
                  }
                }}
                className="inline-flex min-h-[34px] items-center justify-center rounded-[12px] border border-slate-200 bg-slate-50 px-3 text-[12px] font-semibold text-slate-700 shadow-sm"
              >
                History
              </button>
              <Link
                to="/discover"
                className="inline-flex min-h-[34px] items-center justify-center rounded-[12px] border border-slate-200 bg-slate-50 px-3 text-[12px] font-semibold text-slate-700 shadow-sm"
              >
                Book trip
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="mt-5 rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
              Loading your bookings and trip requests...
            </div>
          ) : !activeGroups.length ? (
            <div className="mt-5 rounded-[24px] border border-slate-200 bg-white px-5 py-8 text-center shadow-sm">
              <div className="text-sm font-medium text-slate-900">No active or upcoming bookings</div>
              <div className="mt-1 text-sm text-slate-600">
                When you book a trip, it will appear here.
              </div>
              <Link
                to="/discover"
                className="mt-5 inline-flex rounded-[16px] bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white"
              >
                Browse vehicles
              </Link>
            </div>
          ) : (
            <div className="mt-5 space-y-5">
              {activeGroups.map((g, idx) => {
                const groupStatus = groupStatusMeta(g);
                const endPast = isPlanPast(g);
                const hadFinalBooked = g.items.some(
                  (it) => !isRequestItem(it) && bookedLike(it?.status)
                );
                const allCancelled = g.items.every((it) => isCancelledItem(it));
                const canCancelPlan = !allCancelled && !hasPlanStarted(g) && canCancelScheduledPlan(g);
                const showPlanReview = endPast && hadFinalBooked;
                const countForChip = allCancelled ? totalVehicleCount(g) : activeVehicleCount(g);
                const planSummary = getGroupSummaryAmounts(g);
                const groupedPayments = groupBookingVehiclesByModel(g, vehicleMap);
                const canShowContactCars = isScheduledOpsGroup(g);
                const pickupDate = formatDateWithSuffix(g.pickupDate);
                const dropDate = formatDateWithSuffix(g.dropDate);
                const pickupTime = formatTime12(g.pickupTime);
                const dropTime = formatTime12(g.dropTime);
                const displayPlanKey = getDisplayPlanKey(g, idx);

                return (
                  <article
                    key={`mobile-${g.key}`}
                    className="overflow-hidden rounded-[22px] border border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,1),rgba(248,250,252,0.82))] shadow-[0_14px_32px_rgba(15,23,42,0.06)]"
                  >
                    <div className="p-3.5">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex max-w-[60%] items-center truncate rounded-[10px] bg-slate-900 px-3 py-1.5 text-[11px] font-semibold text-white">
                          {displayPlanKey}
                        </span>
                        <span className="inline-flex items-center rounded-[10px] border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-medium text-slate-700 shadow-sm">
                          {countForChip} {countForChip === 1 ? "car" : "cars"}
                        </span>
                      </div>

                      <div className="mt-3 flex gap-2.5">
                        <MobileStageCard
                          icon={<PickupIcon />}
                          label="Pickup"
                          location={g.pickupLocation || "-"}
                          date={pickupDate}
                          time={pickupTime}
                        />
                        <MobileStageCard
                          icon={<DropIcon />}
                          label="Drop"
                          location={g.destination || "-"}
                          date={dropDate}
                          time={dropTime}
                        />
                      </div>

                      {Array.isArray(g.stops) && g.stops.length ? (
                        <button
                          type="button"
                          onClick={() => setStopsModal({ open: true, group: g })}
                          className="mt-4 flex w-full items-center justify-between rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-left shadow-sm"
                        >
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                              Stops
                            </div>
                            <div className="mt-1 text-sm font-semibold text-slate-900">
                              {g.stops.length} stop{g.stops.length === 1 ? "" : "s"}
                            </div>
                          </div>
                          <span className="text-sm font-semibold text-slate-500">View</span>
                        </button>
                      ) : null}

                      {canShowContactCars ? (
                        <button
                          type="button"
                          onClick={() => setSupplierModal({ open: true, group: g })}
                          className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-[14px] border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-sm"
                        >
                          <span>☎</span>
                          <span>Contact Cars</span>
                        </button>
                      ) : null}

                      <div className="mt-3 rounded-[16px] border border-emerald-200 bg-emerald-50/70 px-4 py-3.5 shadow-sm">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700">
                          Pay in cash to driver
                        </div>
                        <div className="mt-1 text-[22px] font-black leading-none text-emerald-700">
                          {fmtINR(planSummary.pending)}
                        </div>
                        <div className="mt-1 text-[12px] text-slate-600">
                          Total amount yet to be paid
                        </div>
                        <div className="mt-2 space-y-1 text-[12px] text-slate-600">
                          <div>{`Paid already: ${fmtINR(planSummary.paid)}`}</div>
                          <div>{`Total cost: ${fmtINR(planSummary.total)}`}</div>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-slate-200/80 bg-slate-50/60 px-3.5 py-3.5">
                      <div className="space-y-3">
                        {groupedPayments.map((summary) => (
                          <MobileVehicleSummaryCard
                            key={`${g.key}-${summary.key}`}
                            summary={summary}
                            canReview={!canCancelPlan && showPlanReview}
                            onReview={
                              !canCancelPlan && showPlanReview ? () => handleReviewPlan(g) : null
                            }
                          />
                        ))}
                      </div>

                      {canCancelPlan ? (
                        <button
                          type="button"
                          onClick={() => openCancelPlan(g)}
                          className="mt-3 inline-flex min-h-[42px] w-full items-center justify-center rounded-[14px] border border-rose-200 bg-white px-4 text-sm font-semibold text-rose-700 shadow-sm"
                        >
                          Cancel plan
                        </button>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="hidden sm:block">
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">
              {currentTab === "history" ? "Booking History" : "My Bookings"}
            </h1>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (currentTab === "history") {
                    setCurrentTab("active");
                    return;
                  }
                  setCurrentTab("history");
                  if (!historyState.completed.loaded || !historyState.cancelled.loaded) {
                    loadAllHistory({ reset: true }).catch(() => undefined);
                  }
                }}
                className="inline-flex items-center justify-center rounded-[16px] border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
              >
                {currentTab === "history" ? "Back to bookings" : "History"}
              </button>
              <Link
                to="/discover"
                className="inline-flex items-center justify-center rounded-[16px] border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
              >
                Book another trip
              </Link>
            </div>
          </div>

          {loading && currentTab === "active" ? (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-600">Loading your bookings and trip requests...</div>
          ) : !groups.length ? (
            <div className="mt-6 rounded-3xl border border-slate-200 p-10 text-center">
              <div className="text-sm font-medium text-slate-900">
                {currentTab === "history"
                  ? "No booking history yet"
                  : "No active or upcoming bookings"}
              </div>
              <div className="mt-1 text-sm text-slate-600">
                {currentTab === "history"
                  ? "Completed and cancelled bookings will appear here together."
                  : "When you book a trip, it will appear here."}
              </div>
              {currentTab !== "history" ? (
                <Link
                  to="/discover"
                  className="mt-5 inline-flex rounded-2xl bg-black text-white px-5 py-2.5 text-sm font-semibold"
                >
                  Browse vehicles
                </Link>
              ) : null}
            </div>
          ) : (
            <>
              <div className="mt-6 space-y-6">
              {groups.map((g, idx) => {
                const expanded = true;
                const groupStatus = groupStatusMeta(g);

                const endPast = isPlanPast(g);
                const hadFinalBooked = g.items.some(
                  (it) => !isRequestItem(it) && bookedLike(it?.status)
                );
                const allCancelled = g.items.every((it) => isCancelledItem(it));

                // ✅ totals/count for cancelled plans
                const planTotal = allCancelled ? sumGroupFareAll(g) : sumGroupFareActive(g);
                const countForChip = allCancelled ? totalVehicleCount(g) : activeVehicleCount(g);

                const canCancelPlan = !allCancelled && !hasPlanStarted(g) && canCancelScheduledPlan(g);
                const showPlanReview = endPast && hadFinalBooked;

                const pDate = formatDateWithSuffix(g.pickupDate);
                const dDate = formatDateWithSuffix(g.dropDate);
                const pTime = formatTime12(g.pickupTime);
                const dTime = formatTime12(g.dropTime);

                const pickupDisplay =
                  [pDate, pTime].filter((x) => x && x !== "N/A").join(" • ") || "N/A";
                const dropDisplay =
                  [dDate, dTime].filter((x) => x && x !== "N/A").join(" • ") || "N/A";

                const groupedPayments = groupBookingVehiclesByModel(g, vehicleMap);
                const planSummary = getGroupSummaryAmounts(g);
                const canShowContactCars = currentTab === "active" && isScheduledOpsGroup(g);

                return (
                  <article
                    key={g.key}
                    className="overflow-hidden rounded-[24px] border border-slate-200/90 bg-[linear-gradient(180deg,rgba(255,255,255,1),rgba(248,250,252,0.88))] shadow-[0_28px_60px_rgba(15,23,42,0.08)] transition hover:shadow-[0_32px_80px_rgba(15,23,42,0.1)]"
                  >
                    {/* Plan header */}
                    <div className="px-5 md:px-6 pt-5 pb-5">
                      <div className="rounded-[20px] border border-slate-200/80 bg-[radial-gradient(circle_at_top_left,rgba(241,245,249,0.95),rgba(255,255,255,0.98)_45%),linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.88))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="inline-flex items-center rounded-[14px] bg-slate-900 text-white px-3.5 py-1.5 text-xs font-semibold shadow-sm">
                          Plan {idx + 1}
                          </span>

                          <span
                            className={[
                              "inline-flex items-center rounded-[14px] border px-3 py-1.5 text-xs font-medium shadow-sm",
                              groupStatusClass(groupStatus.tone),
                            ].join(" ")}
                          >
                            {groupStatus.label}
                          </span>

                          <span className="inline-flex items-center rounded-[14px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm">
                            {countForChip} {countForChip === 1 ? "vehicle" : "vehicles"}
                          </span>

                          <div className="ml-auto flex items-center gap-3">
                            {canShowContactCars ? (
                              <button
                                type="button"
                                data-no-toggle="true"
                                onClick={() => setSupplierModal({ open: true, group: g })}
                                className="inline-flex h-11 items-center justify-center gap-2 rounded-[16px] border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-sm hover:bg-slate-50"
                              >
                                <span className="text-base leading-none">☎</span>
                                <span>Contact Cars</span>
                              </button>
                            ) : null}
                            <span className="inline-flex min-w-[132px] flex-col rounded-[16px] border border-slate-200 bg-white px-4 py-2 text-right shadow-sm">
                              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                                {allCancelled ? "Trip total (cancelled)" : "Trip total"}
                              </span>
                              <span className="mt-1 text-[24px] font-black leading-none text-slate-900">
                                {fmtINR(planTotal)}
                              </span>
                            </span>

                            {canCancelPlan && (
                              <button
                                type="button"
                                data-no-toggle="true"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openCancelPlan(g);
                                }}
                                className="inline-flex items-center justify-center rounded-[16px] px-4 py-2.5 text-sm font-semibold border border-rose-200 bg-white text-rose-700 shadow-sm hover:bg-rose-50 active:scale-[0.99]"
                              >
                                Cancel plan
                              </button>
                            )}

                            {!canCancelPlan && showPlanReview && (
                              <button
                                type="button"
                                data-no-toggle="true"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReviewPlan(g);
                                }}
                                className="inline-flex items-center justify-center gap-1.5 rounded-[16px] px-4 py-2.5 text-sm font-semibold border border-slate-300 bg-white text-slate-800 shadow-sm hover:bg-slate-50"
                              >
                                <StarOutlineIcon className="h-3.5 w-3.5" />
                                <span>Review plan</span>
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="mt-5 h-px w-full bg-[linear-gradient(90deg,rgba(203,213,225,0.05),rgba(203,213,225,0.95),rgba(203,213,225,0.05))]" />

                        <div className="mt-5 space-y-4">
                          <section className="space-y-3">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                              Trip details
                            </div>
                            <div className="grid gap-4 md:grid-cols-2">
                              <RouteInfoCard
                                icon={<PickupIcon />}
                                label="Pickup"
                                title={g.pickupLocation || "—"}
                                meta={pickupDisplay}
                              />
                              <div className="space-y-4">
                                {g.stops.length > 0 ? (
                                  <button
                                    type="button"
                                    data-no-toggle="true"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setStopsModal({ open: true, group: g });
                                    }}
                                  className="flex w-full items-center justify-between gap-3 rounded-[18px] border border-amber-200/80 bg-[linear-gradient(180deg,rgba(255,251,235,0.95),rgba(255,255,255,0.98))] px-5 py-4 text-left shadow-[0_12px_28px_rgba(245,158,11,0.08)] transition hover:bg-amber-50"
                                >
                                    <div>
                                      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700">
                                        Stops
                                      </div>
                                      <div className="mt-1.5 text-[14px] font-semibold text-slate-900">
                                        {g.stops.length} {g.stops.length === 1 ? "stop" : "stops"} in this route
                                      </div>
                                    </div>
                                    <div className="rounded-[12px] border border-amber-200 bg-white px-3 py-1 text-[12px] font-semibold text-amber-700 shadow-sm">
                                      View all
                                    </div>
                                  </button>
                                ) : null}
                                <RouteInfoCard
                                  icon={<DropIcon />}
                                  label="Drop"
                                  title={g.destination || "—"}
                                  meta={dropDisplay}
                                  accent="emerald"
                                />
                              </div>
                            </div>
                        </section>
                        </div>

                        <div className="mt-5 grid gap-3 md:grid-cols-3">
                          <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-4 shadow-sm">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                              Total fare
                            </div>
                            <div className="mt-2 text-[30px] font-black leading-none tracking-tight text-slate-900">
                              {fmtINR(planSummary.total)}
                            </div>
                            <div className="mt-1 text-[12px] text-slate-500">
                              Full amount across all vehicles
                            </div>
                          </div>
                          <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-4 shadow-sm">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                              Paid already
                            </div>
                            <div className="mt-2 text-[30px] font-black leading-none tracking-tight text-slate-900">
                              {fmtINR(planSummary.paid)}
                            </div>
                            <div className="mt-1 text-[12px] text-slate-500">
                              Amount already collected upfront
                            </div>
                          </div>
                          <div className="rounded-[18px] border border-emerald-200/80 bg-[linear-gradient(180deg,rgba(236,253,245,0.95),rgba(255,255,255,0.98))] px-4 py-4 shadow-[0_14px_30px_rgba(16,185,129,0.08)]">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700">
                              Pending pay to driver
                            </div>
                            <div className="mt-2 text-[30px] font-black leading-none tracking-tight text-emerald-700">
                              {fmtINR(planSummary.pending)}
                            </div>
                            <div className="mt-1 text-[12px] text-emerald-800/70">
                              Balance to be paid after ride completion
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {expanded ? (
                      <div
                        className="border-t border-slate-200/80 bg-[linear-gradient(180deg,rgba(248,250,252,0.55),rgba(255,255,255,0.96))] px-5 py-5 md:px-6"
                        data-no-toggle="true"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                          {groupedPayments.map((summary) => {
                            const perCarPending =
                              summary.eachDriverPending ?? summary.totalPending / Math.max(1, summary.quantity);
                            debugPaidAmounts("render car payment tile", {
                              planKey: g?.key,
                              summary,
                              perCarPending,
                              rendered: {
                                quantity: summary.quantity,
                                perCarPending,
                                totalPending: summary.totalPending,
                                math:
                                  summary.quantity > 1
                                    ? `${fmtINR(perCarPending)} × ${summary.quantity}`
                                    : null,
                              },
                            });
                            return (
                              <div
                                key={summary.key}
                                className="rounded-[22px] border border-slate-200/90 bg-[linear-gradient(180deg,rgba(255,255,255,1),rgba(248,250,252,0.92))] p-5 shadow-[0_20px_42px_rgba(15,23,42,0.08)]"
                              >
                                <div className="min-w-0">
                                  <div className="flex items-start justify-between gap-4">
	                                    <div className="min-w-0">
	                                      <h3 className="text-[22px] font-semibold leading-7 text-slate-900">
	                                        {summary.title}
	                                      </h3>
	                                      {String(summary.refundState || "NONE").toUpperCase() !== "NONE" ? (
	                                        <div
	                                          className={[
	                                            "mt-2 inline-flex items-center rounded-[10px] border px-2.5 py-1 text-[11px] font-semibold",
	                                            refundTone(summary.refundState),
	                                          ].join(" ")}
	                                        >
	                                          {String(summary.refundState || "").toUpperCase() === "COMPLETED"
	                                            ? `Refund completed ${fmtINR(Number(summary.totalRefundAmount || 0))}`
	                                            : String(summary.refundState || "").toUpperCase() === "PENDING"
	                                            ? "Refund in process"
	                                            : "Refund update failed"}
	                                        </div>
	                                      ) : null}
	                                      <div className="mt-3 inline-flex items-center gap-2 rounded-[12px] border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-600 shadow-sm">
	                                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
	                                        {summary.quantity === 1 ? "1 car" : `${summary.quantity} cars`}
	                                      </div>
	                                    </div>
                                    <div className="rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-right shadow-sm">
                                      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                                        Quantity
                                      </div>
                                      <div className="mt-1 text-[24px] font-black leading-none text-slate-900">
                                        {summary.quantity}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="mt-5 rounded-[18px] border border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.92))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]">
                                    <div className="grid gap-3 sm:grid-cols-2">
                                      <div className="rounded-[16px] border border-slate-200 bg-white px-4 py-4 shadow-sm">
                                        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                                          Pay to driver per car
                                        </div>
                                        <div className="mt-3 text-[28px] font-black leading-none tracking-tight text-slate-900">
                                          {fmtINR(perCarPending)}
                                        </div>
                                        <div className="mt-2 text-[12px] text-slate-500">
                                          Single vehicle balance
                                        </div>
                                      </div>

                                      <div className="rounded-[16px] border border-emerald-200/80 bg-[linear-gradient(180deg,rgba(236,253,245,0.95),rgba(255,255,255,0.98))] px-4 py-4 shadow-sm">
                                        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700">
                                          Total pay to driver
                                        </div>
                                        <div className="mt-3 text-[28px] font-black leading-none tracking-tight text-emerald-700">
                                          {fmtINR(summary.totalPending)}
                                        </div>
                                        <div className="mt-2 text-[12px] text-emerald-800/70">
                                          Plan share for this vehicle type
                                        </div>
                                      </div>
                                    </div>

                                    <div className="mt-4 rounded-[16px] border border-dashed border-slate-300 bg-white px-4 py-3">
                                      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                                        Calculation
                                      </div>
                                      <div className="mt-2 flex items-end justify-between gap-3">
                                        <div>
                                          <div className="text-[18px] font-bold text-slate-900">
                                            {summary.quantity > 1
                                              ? `${fmtINR(perCarPending)} × ${summary.quantity}`
                                              : fmtINR(perCarPending)}
                                          </div>
                                          <div className="mt-1 text-[12px] text-slate-500">
                                            {summary.quantity > 1
                                              ? "Per-car amount multiplied by quantity"
                                              : "Per-car amount equals total amount"}
                                          </div>
                                        </div>
                                        <div className="text-right">
                                          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                                            Total
                                          </div>
                                          <div className="mt-1 text-[22px] font-black leading-none text-slate-900">
                                            {fmtINR(summary.totalPending)}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </article>
                );
              })}
              </div>

              {currentTab === "history" &&
              (historyState.completed.hasMore || historyState.cancelled.hasMore) ? (
                <div className="mt-5 flex justify-center">
                  <button
                    type="button"
                    onClick={() => loadAllHistory()}
                    className="inline-flex rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    disabled={historyState.completed.loading || historyState.cancelled.loading}
                  >
                    {historyState.completed.loading || historyState.cancelled.loading
                      ? "Loading..."
                      : "Load more"}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </section>
      </main>

      {mobileHistoryOpen ? (
        <div className="fixed inset-0 z-[72] bg-white sm:hidden">
          <div className="flex h-full flex-col bg-white">
            <div className="border-b border-slate-200 px-4 py-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-[22px] font-semibold text-slate-900">Booking History</h2>
                <button
                  type="button"
                  onClick={() => setMobileHistoryOpen(false)}
                  className="inline-flex min-h-[34px] items-center justify-center rounded-[12px] border border-slate-200 bg-slate-50 px-3 text-[12px] font-semibold text-slate-700"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {mobileHistoryPending ? (
                <div className="flex min-h-[40vh] items-center justify-center">
                  <div className="rounded-[20px] border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-600 shadow-sm">
                    Loading booking history...
                  </div>
                </div>
              ) : !historyState.completed.groups.length && !historyState.cancelled.groups.length ? (
                <div className="rounded-[22px] border border-slate-200 bg-white px-5 py-8 text-center shadow-sm">
                  <div className="text-sm font-medium text-slate-900">No booking history yet</div>
                  <div className="mt-1 text-sm text-slate-600">
                    Completed and cancelled bookings will appear here together.
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {mobileHistoryGroups.map((g, idx) => {
                    const allCancelled = g.items.every((it) => isCancelledItem(it));
                    const planTotal = allCancelled ? sumGroupFareAll(g) : sumGroupFareActive(g);
                    const countForChip = allCancelled ? totalVehicleCount(g) : activeVehicleCount(g);
                    const groupedPayments = groupBookingVehiclesByModel(g, vehicleMap);
                    const displayPlanKey = getDisplayPlanKey(g, idx);
                    return (
                      <article
                        key={`history-mobile-${g.key}`}
                        className="overflow-hidden rounded-[24px] border border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,1),rgba(248,250,252,0.82))] shadow-sm"
                      >
                        <div className="p-4">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex max-w-[60%] items-center truncate rounded-[10px] bg-slate-900 px-3 py-1.5 text-[11px] font-semibold text-white">
                              {displayPlanKey}
                            </span>
                            <span className="inline-flex items-center rounded-[10px] border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-medium text-slate-700 shadow-sm">
                              {countForChip} {countForChip === 1 ? "car" : "cars"}
                            </span>
                          </div>
                          <div className="mt-4 flex gap-3">
                            <MobileStageCard
                              icon={<PickupIcon />}
                              label="Pickup"
                              location={g.pickupLocation || "-"}
                              date={formatDateWithSuffix(g.pickupDate)}
                              time={formatTime12(g.pickupTime)}
                            />
                            <MobileStageCard
                              icon={<DropIcon />}
                              label="Drop"
                              location={g.destination || "-"}
                              date={formatDateWithSuffix(g.dropDate)}
                              time={formatTime12(g.dropTime)}
                            />
                          </div>
                          <div className="mt-4 rounded-[16px] border border-slate-200 bg-white px-4 py-3 shadow-sm">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                              {allCancelled ? "Trip total (cancelled)" : "Trip total"}
                            </div>
                            <div className="mt-1 text-[24px] font-black leading-none text-slate-900">
                              {fmtINR(planTotal)}
                            </div>
                          </div>
                        </div>
                        <div className="border-t border-slate-200/80 bg-slate-50/60 px-4 py-4">
                          <div className="space-y-3">
                            {groupedPayments.map((summary) => (
                              <MobileVehicleSummaryCard
                                key={`history-${g.key}-${summary.key}`}
                                summary={summary}
                              />
                            ))}
                          </div>
                        </div>
                      </article>
                    );
                  })}

                  {(historyState.completed.hasMore || historyState.cancelled.hasMore) ? (
                    <button
                      type="button"
                      onClick={() => loadAllHistory()}
                      className="w-full rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm disabled:opacity-60"
                      disabled={mobileHistoryPending}
                    >
                      {mobileHistoryPending ? "Loading..." : "Load more"}
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {stopsModal.open && stopsModal.group ? (
        <div className="fixed inset-0 z-[74] flex items-end justify-center bg-black/40 px-4 py-6 sm:items-center">
          <div className="w-full max-w-2xl rounded-[28px] border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start gap-4 border-b border-slate-200 px-5 py-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-700">
                <span className="text-xl">•</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[22px] font-extrabold text-slate-900">All stops</div>
                <div className="mt-1 text-sm leading-6 text-slate-600">
                  {stopsModal.group.stops.length} intermediate{" "}
                  {stopsModal.group.stops.length === 1 ? "stop" : "stops"} between pickup and drop.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setStopsModal({ open: false, group: null })}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                ×
              </button>
            </div>

            <div className="max-h-[70vh] space-y-3 overflow-auto px-5 py-5">
              {stopsModal.group.stops.map((stop, stopIdx) => (
                <div
                  key={`${stopsModal.group.key}-stop-modal-${stopIdx}`}
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                >
                  <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                    Stop {stopIdx + 1}
                  </div>
                  <div className="mt-1 text-[15px] font-semibold leading-6 text-slate-900">{stop}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {supplierModal.open && supplierModal.group ? (
        <div className="fixed inset-0 z-[75] flex items-end justify-center bg-black/40 px-4 py-6">
          <div className="w-full max-w-2xl rounded-[28px] border border-slate-200 bg-white shadow-2xl">
            {(() => {
              const suppliers = buildScheduledPlanSuppliers(supplierModal.group, vehicleMap);
              const alignment = getScheduledPlanAlignmentSummary(supplierModal.group);
              const contactDetailsVisible = areScheduledContactDetailsVisible(
                supplierModal.group,
                scheduledContactVisibleBeforePickupHours
              );
              const visibilityMessage = scheduledContactVisibilityMessage(
                scheduledContactVisibleBeforePickupHours
              );
              return (
                <>
                  <div className="flex items-start gap-4 border-b border-slate-200 px-5 py-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-pink-50 text-pink-600">
                      <span className="text-xl">☎</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[22px] font-extrabold text-slate-900">Contact Cars</div>
                      <div className="mt-1 text-sm leading-6 text-slate-600">
                        {contactDetailsVisible
                          ? "Contact the available supplier or driver for this plan."
                          : visibilityMessage}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSupplierModal({ open: false, group: null })}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50"
                    >
                      ×
                    </button>
                  </div>

                  <div className="max-h-[70vh] space-y-4 overflow-auto px-5 py-5">
                    <button
                      type="button"
                      onClick={() => {
                        window.location.href = "tel:9065152425";
                      }}
                      className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-[16px] border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-sm sm:hidden"
                    >
                      <span>☎</span>
                      <span>Need help? Call us</span>
                    </button>
                    {!contactDetailsVisible ? (
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
                        <div className="text-[15px] font-bold text-emerald-800">
                          Contacts will appear here shortly
                        </div>
                        <div className="mt-2 text-sm leading-6 text-slate-700">
                          {alignment.pending > 0
                            ? `Contact for ${alignment.pending} ${alignment.pending === 1 ? "car" : "cars"} will be visible soon.`
                            : "No linked contact details are available yet."}
                        </div>
                      </div>
                    ) : null}

                    {contactDetailsVisible
                      ? suppliers.map((supplier) => (
                          <div
                            key={supplier.key}
                            className="rounded-[24px] border border-slate-200 bg-slate-50 p-4"
                          >
                            <div className="flex flex-wrap items-start gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="text-[16px] font-bold text-slate-900">
                                  {supplier.supplierName}
                                </div>
                                {supplier.supplierPhone ? (
                                  <div className="mt-1 text-sm text-slate-500">
                                    {supplier.supplierPhone}
                                  </div>
                                ) : null}
                              </div>
                              {supplier.supplierPhone ? (
                                <a
                                  href={`tel:${String(supplier.supplierPhone).replace(/\D/g, "")}`}
                                  className="inline-flex h-12 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                                >
                                  Contact
                                </a>
                              ) : null}
                            </div>
                            <div className="mt-4 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
                              {supplier.items.map((item, idx) => (
                                <div
                                  key={`${supplier.key}-${item.title}-${idx}`}
                                  className="flex items-center gap-3 px-4 py-3"
                                >
                                  <div className="min-w-0 flex-1 text-[14px] font-semibold text-slate-900">
                                    {item.title}
                                  </div>
                                  <div className="rounded-xl bg-slate-100 px-3 py-2 text-[12px] font-bold text-slate-800">
                                    {item.quantity} {item.quantity === 1 ? "car" : "cars"}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      : null}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      ) : null}

      {/* Cancel plan confirmation */}
      {planToCancel && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 p-5">
            <h2 className="text-[17px] font-semibold text-slate-900">Cancel this trip plan?</h2>
            <p className="mt-1 text-sm text-slate-600">
              All active items in this plan will be cancelled (requests + bookings that haven’t started).
            </p>

            <div className="mt-3 max-h-48 overflow-auto space-y-2">
              {planToCancel.items
                .filter((it) => !isCancelledItem(it))
                .filter((it) => canCancelScheduledItem(it))
                .map((it, idx) => {
                  const vid = getItemVehicleId(it);
                  const veh = vehicleMap[vid] || it.vehicle || {};
                  const name = `${veh.brand || ""} ${veh.model || ""}`.trim() || "—";

                  const rawReg = veh.registrationNumber || "";
                  const reg = shouldShowRegistrationNumber(it) && rawReg ? ` • ${rawReg}` : "";

                  const label = isRequestItem(it) ? `Request #${it.requestId}` : `Booking #${it.id}`;

                  return (
                    <div
                      key={`${label}-${idx}`}
                      className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[13px] text-slate-800"
                    >
                      <span className="h-2 w-2 rounded-full bg-slate-400" />
                      <span className="truncate">
                        {name}
                        {reg}
                      </span>
                      <span className="ml-auto text-xs text-slate-500">{label}</span>
                    </div>
                  );
                })}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (planCancelBusy) return;
                  setPlanToCancel(null);
                }}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={actuallyCancelPlan}
                className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                disabled={planCancelBusy}
              >
                {planCancelBusy ? "Cancelling…" : "Cancel plan"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="sm:hidden border-t border-slate-200 bg-white px-4 py-6">
        <img
          src="/Wayroot (1600 x 500 px).svg"
          alt="Wayroot"
          className="h-9 w-auto"
          draggable={false}
        />
      </div>
      <div className="hidden sm:block">
        <Footer />
      </div>
    </div>
  );
}
